import { cx } from '@flexbyt/widget-preview';
import { useRef, type ElementType, type ReactNode } from 'react';
import { useReveal } from '../site/reveal.js';
import { PlayJet } from '../mascot/scenes.js';

/**
 * Where the page's edges are, and how far apart its sections sit.
 *
 * ---------------------------------------------------------------------------
 * Why these are components rather than classes
 * ---------------------------------------------------------------------------
 * Vertical rhythm is the thing that separates a designed page from an
 * assembled one, and it is also the easiest thing to lose: every section gets
 * `py-20` except the two that got `py-24` because they looked cramped that
 * afternoon. Nobody notices in review — you only see it scrolling the whole
 * page, by which point there are thirty sections.
 *
 * So spacing is a decision made once, here, and sections ask for a size rather
 * than a number.
 */

const WIDTHS = {
  /* Body copy. ~68 characters, which is about where a line stops being
     comfortable to read. Most marketing sites set prose to the full container
     width because the container was the only measurement they had. */
  prose: 'max-w-prose',
  /* A heading and its supporting paragraph. Wider than prose, because a display
     heading broken every six words reads as a list. */
  text: 'max-w-text',
  /* The default. Everything structural lines up on this. */
  page: 'max-w-page',
  /* For content that genuinely needs the room — a widget gallery, a wide
     product still. Used rarely; a page whose edges move around has no edges. */
  wide: 'max-w-wide',
  /* No constraint. For a band whose *background* runs edge to edge; the
     content inside it still gets its own container. */
  full: '',
} as const;

export type ContainerWidth = keyof typeof WIDTHS;

/**
 * Horizontal padding, at three sizes.
 *
 * Not `px-4 md:px-6 lg:px-8` written out at every call site. The gutter is part
 * of the grid, and on a phone it is the difference between text that feels
 * placed and text that feels crammed against the glass.
 */
export const GUTTER = 'px-5 sm:px-6 lg:px-8';

export function Container({
  children,
  width = 'page',
  className,
  as: Tag = 'div',
  'data-reveal': reveal,
}: {
  children: ReactNode;
  width?: ContainerWidth;
  className?: string;
  as?: ElementType;
  'data-reveal'?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  useReveal(ref, reveal !== undefined);

  return (
    <Tag ref={ref} className={cx('mx-auto w-full', WIDTHS[width], GUTTER, className)} data-reveal={reveal}>
      {children}
    </Tag>
  );
}

const TONES = {
  /* The page. Most sections are this, and that is the point — a surface should
     mean "a different kind of thing", and it means nothing if everything has
     one. */
  page: 'bg-white text-ink-900',
  /* One step off white, for a section that needs separating from its
     neighbours without a rule between them. */
  raised: 'bg-surface text-ink-900',
  /*
     A tinted ground, for the one section on a page that is its turning point.

     Used at most once. The homepage alternated white and `ink-25` four times,
     which is a rhythm so even that it stops being read — every band looks like
     the last one, and nothing on the page says "this is the part that
     matters". A single tinted band breaks that evenness and marks the payoff.

     `accent-50` is the lightest step on the ramp, and it needs to stay that
     way: this is a ground for other things to sit on, not a coloured panel. If
     a white card on it does not still read as lifted, the tint is too strong.
  */
  tinted: 'bg-brand-wash text-ink-900',
  /* The full stop. Near-black, used once or twice on a page at most — the
     closing call to action, or a single band that should feel like a held
     breath. `on-ink` switches the selection colour and gives descendants a hook
     for the inverted palette. */
  ink: 'on-ink grain relative overflow-hidden bg-night text-white',
} as const;

export type SectionTone = keyof typeof TONES;

/**
 * A full-width band with contained content.
 *
 * The tone paints the whole width of the viewport while `Container` keeps the
 * words where the rest of the page's words are. Getting this wrong in the other
 * direction — a coloured box with rounded corners floating inside the container
 * — is most of what makes a page look like a stack of components rather than a
 * document.
 */
export function Section({
  children,
  tone = 'page',
  width = 'page',
  size = 'base',
  className,
  containerClassName,
  as: Tag = 'section',
  id,
  'aria-labelledby': labelledBy,
  reveal = true,
}: {
  children: ReactNode;
  tone?: SectionTone;
  width?: ContainerWidth;
  /** `tight` for a section that continues the one above; `loose` to isolate. */
  size?: 'tight' | 'base' | 'loose';
  className?: string;
  containerClassName?: string;
  as?: ElementType;
  id?: string;
  'aria-labelledby'?: string;
  /**
   * Rise into place on first scroll into view (see `site/reveal.ts`). Off for
   * a hero, which has its own entrance and is on screen from the start.
   */
  reveal?: boolean;
}) {
  const padding = {
    tight: 'py-12 md:py-16',
    base: 'py-16 md:py-24',
    loose: 'py-20 md:py-32',
  }[size];

  return (
    <Tag
      id={id}
      aria-labelledby={labelledBy}
      data-nav-theme={tone === 'ink' ? 'dark' : undefined}
      className={cx(TONES[tone], padding, 'overflow-x-clip', className)}
    >
      {tone === 'ink' ? <NightGlow /> : null}
      <Container
        width={width}
        className={cx('relative', containerClassName)}
        data-reveal={reveal ? 'auto' : undefined}
      >
        {children}
        {tone === 'ink' ? <NightJet /> : null}
      </Container>
    </Tag>
  );
}

/**
 * A hairline.
 *
 * `ink-150` and not `ink-200`: at a full section's width the lighter value
 * disappears and the heavier one reads as a drawn line rather than an edge —
 * which is the same reason the shared tokens carry a 150 step at all.
 */
/**
 * The flexbyt.com night atmosphere: a faint grid, a brand glow and a grape
 * glow. Drawn behind every `ink` band so the dark sections share one light.
 */
export function NightGlow() {
  return (
    <>
      <div
        aria-hidden
        className="grid-fade pointer-events-none absolute inset-0 -z-10 [--grid-line:rgba(255,255,255,0.05)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-24 -z-10 size-[30rem] rounded-full bg-brand/30 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -bottom-32 -z-10 size-[30rem] rounded-full bg-grape/30 blur-[120px]"
      />
    </>
  );
}

/**
 * Jet, beside the closing call to action.
 *
 * Every ink band on the site is a page's last word — a heading, a sentence,
 * two buttons — set to the reading measure, which left the right half of the
 * band empty at desktop widths. Jet fills it, cheering on the decision the
 * band is asking for. Below `lg` the words have the whole width and Jet steps
 * aside.
 */
function NightJet() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-y-0 right-8 hidden items-center lg:flex xl:right-16">
      <div className="relative">
        <div className="absolute inset-[-20%] -z-10 rounded-full bg-brand/25 blur-[70px]" />
        <PlayJet mood="cheer" className="pointer-events-auto w-40 xl:w-48" />
      </div>
    </div>
  );
}

export function Rule({ className }: { className?: string }) {
  return <hr className={cx('border-0 border-t border-ink-150', className)} />;
}

/**
 * A reading measure *inside* a section, rather than a narrower container.
 *
 * ---------------------------------------------------------------------------
 * This is the difference between an editorial page and a stack of components
 * ---------------------------------------------------------------------------
 * The obvious way to set a heading and its paragraph to a comfortable width is
 * to give that section a narrower container. It is also wrong, and the reason
 * is only visible once the page is long: a 44rem container centred in the
 * viewport has a different left edge from a 72rem one, so the hero starts a
 * hundred pixels further in than the section beneath it, and further in again
 * than the header above it. Nothing lines up vertically, and the page reads as
 * a series of separately-centred blocks — which is exactly what it is.
 *
 * A publication has one left margin and holds it down the whole page. So every
 * section keeps the same container, and text is constrained *within* it by this
 * — left-aligned, sharing that margin.
 *
 * `Container width="text"` still exists for the genuinely centred page: a 404,
 * a legal document, a confirmation screen. Not for a section in a flow.
 */
export function Measure({
  children,
  size = 'text',
  className,
}: {
  children: ReactNode;
  size?: 'prose' | 'text';
  className?: string;
}) {
  return (
    <div className={cx(size === 'prose' ? 'max-w-prose' : 'max-w-text', className)}>
      {children}
    </div>
  );
}
